import type { CostEstimateItem, QuoteItem } from '@elka/types';

// Restore a boq-with-prices version: copy the per-line client prices from a quote
// snapshot back onto the cost estimate's items.
//
// For boq-with-prices the client-facing price is owned by the COST ESTIMATE
// (item.clientPrice), and the תמחור editor re-derives every quote line from it on load
// (see buildSyncedQuoteItems). The quote snapshot stores that same price as the line's
// unitPrice, so "restoring a version" means writing those prices back onto the estimate —
// otherwise the next editor sync silently reverts the restored draft.
//
// Pairing is by stable itemId, never by array position: the estimate and the snapshot can
// legitimately diverge in length/order. Headers carry no price and are left untouched, as
// are estimate rows the snapshot doesn't mention. Returns `changed: false` when nothing
// moved so the caller can skip a no-op Firestore write.
export function applyRestoredClientPrices(
  estimateItems: CostEstimateItem[],
  sourceQuoteItems: QuoteItem[]
): { items: CostEstimateItem[]; changed: boolean } {
  const priceByItemId = new Map<string, number>();
  for (const it of sourceQuoteItems) {
    if (it.isHeader || it.itemId == null) continue;
    priceByItemId.set(it.itemId, it.unitPrice || 0);
  }

  let changed = false;
  const items = estimateItems.map((it) => {
    if (it.isHeader) return it;
    const restored = priceByItemId.get(it.itemId);
    if (restored == null || restored === (it.clientPrice ?? 0)) return it;
    changed = true;
    return { ...it, clientPrice: restored };
  });

  return { items, changed };
}
